<template>
  <DialogWrapper
    close-on-cancel
    class="add-comment-dialog"
    saveBtnLabelI18nKey="common.btn.submit"
    :show.sync="show"
    :loading="loading"
    :titleI18nKey="`${i18nPrefix}.title.addComment`"
    @save="handleSave"
  >
    <WrappedForm
      ref="form"
      :model="commentInfo"
      :formLines="formLines"
    >
      <template #isPrivate>
        <el-switch
          v-model="commentInfo.isPrivate"
          class="is-private"
          active-color="#0096cc"
          :active-text="$t('common.yes')"
          :inactive-text="$t('common.no')"
          :width="switchWidth"
        ></el-switch>
      </template>
      <template #recruiter>
        <Assign
          labelI18nKey="admin.talent.label.recruiterAccess"
          :showLabel="false"
          :selectIds.sync="currentOwnerInfo.account_ids"
          :optionList="accountList"
        ></Assign>
      </template>
      <template #role>
        <Assign
          labelI18nKey="admin.talent.label.roleAccess"
          :showLabel="false"
          :selectIds.sync="currentOwnerInfo.role_ids"
          :optionList="roleList"
        ></Assign>
      </template>
      <template #interviewerCanView>
        <el-switch
          v-model="commentInfo.interviewerCanView"
          class="interview-can-view"
          active-color="#0096cc"
          :active-text="$t('common.yes')"
          :inactive-text="$t('common.no')"
          :width="switchWidth"
        ></el-switch>
      </template>
    </WrappedForm>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import { mapActions, mapGetters } from 'vuex'
import Assign from '../../../talentPool/components/assign.vue'

export default {
  components: {
    DialogWrapper,
    WrappedForm,
    Assign
  },
  props: {
    jobId: {
      required: false
    },
    candidateId: {
      required: true
    },
    isTalentPool: {
      type: Boolean,
      default: false
    },
    showInterviewerCanView: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      show: false,
      loading: false,
      commentInfo: {
        comment: null,
        isPrivate: false,
        interviewerCanView: false,
        accountIds: [],
        roleIds: []
      },
      isPrivateLocked: false,
      accountList: [],
      roleList: [],
      currentOwnerInfo: {
        account_ids: [],
        role_ids: []
      }
    }
  },
  computed: {
    formLines () {
      const i18nPrefix = this.i18nPrefix
      const requiredMessage = this.$t('common.message.requiredFormField')
      const lines = {
        comment: {
          label: this.$t(`${i18nPrefix}.label.comment`),
          props: { type: 'textarea', autosize: { minRows: 5 } },
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        accountIds: {
          label: this.$t('admin.talent.label.recruiterAccess'),
          contentSlot: 'recruiter'
        },
        roleIds: {
          label: this.$t('admin.talent.label.roleAccess'),
          contentSlot: 'role'
        },
        interviewerCanView: {
          hidden: !this.showInterviewerCanView,
          label: this.$t(`${i18nPrefix}.label.interviewerCanView`),
          contentSlot: 'interviewerCanView',
          class: 'interview-can-view'
        },
        isPrivate: {
          hidden: this.hasCommentOwner,
          contentSlot: 'isPrivate',
          label: this.$t(`${i18nPrefix}.label.isPrivate`),
          class: 'is-private'
        }
      }
      return lines
    },
    form () {
      return this.$refs.form
    },
    hasCommentOwner () {
      return (this.currentOwnerInfo?.account_ids?.length || 0) > 0 || (this.currentOwnerInfo?.role_ids?.length || 0) > 0
    },
    switchWidth () {
      return this.$i18n.locale === 'en' ? 55 : 50
    }
  },
  created () {

  },
  methods: {
    ...mapActions('application', ['addComment']),
    ...mapActions('talent', ['createTalentPoolComment']),
    ...mapActions('company', ['getTeamMemberList']),
    ...mapActions('roleManagement', ['getRoleList']),
    ...mapGetters('account', ['getCurrentAccount']),
    openDialog () {
      this.show = true
      this.loadAccountList()
      this.loadRoleList()
    },
    clearForm () {
      this.commentInfo = {
        comment: null,
        isPrivate: false,
        interviewerCanView: false,
        ownerInfo: null
      }
      this.currentOwnerInfo = {
        account_ids: [],
        role_ids: []
      }
      this.isPrivateLocked = false
      this.$nextTick(() => {
        this.form.clearValidate()
      })
    },
    async handleSave () {
      if (this.loading) {
        return
      }
      const isValid = await this.form.submit()
      if (!isValid) {
        return
      }
      this.loading = true
      let resp = null
      try {
        if (!this.isTalentPool) {
          const commentInfo = {
            job_id: this.jobId,
            candidate_id: this.candidateId,
            comment: this.commentInfo.comment,
            is_private: this.hasCommentOwner || this.commentInfo.isPrivate,
            owner_info: this.currentOwnerInfo,
            enable_assign: true
          }
          if (this.showInterviewerCanView) {
            commentInfo.interviewer_can_view = this.commentInfo.interviewerCanView
          }
          resp = await this.addComment(commentInfo)
        } else {
          const commentInfo = {
            candidate_id: this.candidateId,
            comment: this.commentInfo.comment,
            is_private: this.hasCommentOwner || this.commentInfo.isPrivate,
            owner_info: this.currentOwnerInfo
          }
          resp = await this.createTalentPoolComment(commentInfo)
        }
        if (resp) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.addCommentSuccess`))
          this.show = false
          this.$emit('added', this.commentInfo.comment)
        } else {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.addCommentFail`))
        }
      } finally {
        this.loading = false
      }
    },
    handleChangeCommentOwner (info, callback) {
      const accountIds = _.get(info, 'account_ids', [])
      const roleIds = _.get(info, 'role_ids', [])
      if (accountIds?.length || roleIds?.length) {
        this.commentInfo.isPrivate = true
      }
      if (this.currentOwnerInfo !== info) {
        this.currentOwnerInfo = info
      }
      callback && callback()
    },
    async loadAccountList () {
      const account = this.getCurrentAccount()
      const params = {
        companyId: account.root_company_id,
        is_all: true,
        exclude_disabled: true
      }
      const resp = await this.getTeamMemberList(params)
      if (resp) {
        this.accountList = _.orderBy(resp.results || [], [item => (item.name || '').toLowerCase()], ['asc'])
      }
    },
    async loadRoleList () {
      const resp = await this.getRoleList()
      if (resp) {
        this.roleList = _.map(resp.role_list || [], role => ({
          id: role.id,
          name: role.title
        }))
      }
    }
  },
  watch: {
    show () {
      if (this.show) {
        this.clearForm()
      }
    },
    currentOwnerInfo: {
      handler (val) {
        this.handleChangeCommentOwner(val)
      },
      deep: true,
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.add-comment-dialog {
  ::v-deep {
    .form-container {
      .is-private {
        &.el-form-item {
         .el-form-item__label {
            .label-inner {
              margin-top: 0;
              line-height: 32px;
              height: 32px;
            }
          }
        }

        .el-switch {
          .el-switch__label {
            display: none;
            position: absolute;
            height: 100%;
            color: #ffffff;
            z-index: 1;
            margin: 0;

            &.is-active {
              display: inline-block;
            }
          }

          .el-switch__label--left {
            left: 25px;
          }

          .el-switch__core {
            width: 55px;
          }

          .el-switch__label--right {
            left: 10px;
          }
        }
      }

      .interview-can-view {
        &.el-form-item {
         .el-form-item__label {
            .label-inner {
              margin-top: 0;
              line-height: 32px;
              height: 32px;
            }
          }
        }

        .el-switch {
          .el-switch__label {
            display: none;
            position: absolute;
            height: 100%;
            color: #ffffff;
            z-index: 1;
            margin: 0;

            &.is-active {
              display: inline-block;
            }
          }

          .el-switch__label--left {
            left: 25px;
          }

          .el-switch__core {
            width: 55px;
          }

          .el-switch__label--right {
            left: 10px;
          }
        }
      }
    }
  }
}
</style>
